import React, { useMemo, useState } from "react";
import { Breadcrumb, Button, Icon, message, Modal, Table } from "antd";
import styles from "./index.module.scss";
import { ColumnProps } from "antd/es/table";
import { ModalProps } from "antd/es/modal";
import useRequest from "../../../../../../utils/useRequest";
import {
    downloadVideoUseThunder,
    videoList,
    VideoListItem,
    VideoListItemPart
} from "../../../../../../service/course";
import { unstable_batchedUpdates } from "react-dom";
import { errorMessage, toNumber } from "../../../../../../service/service";
import useRequestFn from "../../../../../../utils/useRequestFn";
import { LinkButton } from "../LinkButton";
import { ModalHeader } from "../ModalHeader";

export function DownloadModal({ ...props }: ModalProps) {
    const [groupByType, setGroupByType] = useState(1);

    const [batchNo, setBatchNo] = useState("");
    const [courseNo, setCourseNo] = useState("");
    const [courseSeq, setCourseSeq] = useState("");

    const [batchName, setBatchName] = useState("");
    const [courseName, setCourseName] = useState("");

    const [data, { loading }] = useRequest(
        () => videoList({ groupByType, batchNo, courseNo, courseSeq }),
        {},
        [groupByType, batchNo, courseNo, courseSeq]
    );

    const [onDownload, downloadStatus] = useRequestFn(
        async (downType: number, data: VideoListItemPart) => {
            try {
                await downloadVideoUseThunder(downType, {
                    groupByType,
                    ...data
                });
            } catch (e) {
                message.error(errorMessage(e));
            }
        }
    );

    function onDownloadAll() {
        onDownload(1, {
            batchNo: "",
            courseNo: "",
            courseSeq: ""
        });
    }

    const count = useMemo(() => {
        if (!data) return 0;
        return data.reduce((count, item) => {
            return (
                count +
                toNumber(item.tripleScreenDisplayNum) +
                toNumber(item.screenPlaybackNum)
            );
        }, 0);
    }, [data]);

    const columns: ColumnProps<VideoListItem>[] = [
        {
            title: "文件名称",
            dataIndex: "fileName",
            render: (fileName, item) => (
                <LinkButton
                    icon={
                        <Icon
                            type="folder"
                            style={{ color: "rgb(255,215,100)", fontSize: 18 }}
                        />
                    }
                    disabled={groupByType === 3}
                    onClick={() => {
                        if (groupByType === 3) return;
                        if (loading) return;

                        unstable_batchedUpdates(() => {
                            setGroupByType(num => num + 1);
                            setBatchNo(item.batchNo);
                            setCourseNo(item.courseNo);
                            setCourseSeq(item.courseSeq);

                            if (groupByType === 1) {
                                setBatchName(item.fileName);
                            }

                            if (groupByType === 2) {
                                setCourseName(item.fileName);
                            }
                        });
                    }}>
                    {fileName}
                </LinkButton>
            )
        },
        {
            title: "文件夹数",
            dataIndex: "fileNum",
            width: 100,
            align: "center",
            render: num => num || 0
        },
        {
            title: "三分屏视频数",
            dataIndex: "tripleScreenDisplayNum",
            width: 150,
            align: "center"
        },
        {
            title: "屏幕共享回放数",
            dataIndex: "screenPlaybackNum",
            width: 150,
            align: "center"
        },
        {
            title: "操作",
            width: 500,
            align: "center",
            render: (_, item) => {
                if (groupByType === 3) {
                    const downType = item.vodType === "1" ? 2 : 3;

                    return (
                        <Button
                            type="link"
                            disabled={
                                item.tripleScreenDisplayNum === 0 &&
                                item.screenPlaybackNum === 0
                            }
                            onClick={() =>
                                onDownload(downType, {
                                    courseNo,
                                    courseSeq,
                                    batchNo
                                })
                            }>
                            下载全部视频
                        </Button>
                    );
                }

                return (
                    <>
                        <Button
                            type="link"
                            disabled={
                                item.tripleScreenDisplayNum === 0 &&
                                item.screenPlaybackNum === 0
                            }
                            onClick={() => onDownload(1, item)}>
                            下载全部视频
                        </Button>

                        <Button
                            type="link"
                            disabled={item.tripleScreenDisplayNum === 0}
                            onClick={() => onDownload(2, item)}>
                            下载三分屏回放视频
                        </Button>

                        <Button
                            type="link"
                            disabled={item.screenPlaybackNum === 0}
                            onClick={() => onDownload(3, item)}>
                            下载屏幕共享回放视频
                        </Button>
                    </>
                );
            }
        }
    ];

    return (
        <Modal
            title={<ModalHeader />}
            visible
            centered
            footer={false}
            width={1400}
            {...props}>
            <div className={styles.root}>
                <div className={styles.tip}>
                    温馨提示，为了更快下载视频，建议您下载迅雷，然后利用迅雷下载具体页面的全部视频。点击链接查看{" "}
                    <a href="/assets/迅雷安装手册.docx" target="_blank">
                        《迅雷安装手册》
                    </a>
                    、
                    <a
                        href="/assets/直播回放视频下载使用说明.docx"
                        target="_blank">
                        《视频下载使用说明》
                    </a>
                    。
                </div>

                <div className={styles.tools}>
                    <Breadcrumb>
                        <Breadcrumb.Item>
                            <LinkButton
                                onClick={() => {
                                    if (loading || downloadStatus.loading)
                                        return;
                                    unstable_batchedUpdates(() => {
                                        setGroupByType(1);
                                        setBatchName("");
                                        setCourseName("");
                                        setBatchNo("");
                                        setCourseNo("");
                                        setCourseSeq("");
                                    });
                                }}>
                                全部文件
                            </LinkButton>
                        </Breadcrumb.Item>

                        {batchName && (
                            <Breadcrumb.Item>
                                <LinkButton
                                    disabled={groupByType === 2}
                                    onClick={() => {
                                        if (loading || downloadStatus.loading)
                                            return;
                                        unstable_batchedUpdates(() => {
                                            setGroupByType(2);
                                            setCourseName("");
                                            setCourseNo("");
                                            setCourseSeq("");
                                        });
                                    }}>
                                    {batchName}
                                </LinkButton>
                            </Breadcrumb.Item>
                        )}

                        {courseName && (
                            <Breadcrumb.Item>{courseName}</Breadcrumb.Item>
                        )}
                    </Breadcrumb>

                    <div>已全部加载，共{count}个视频文件</div>

                    <Button
                        style={{ marginLeft: "auto" }}
                        type="primary"
                        ghost
                        onClick={onDownloadAll}
                        disabled={loading || downloadStatus.loading}>
                        一键下载全部视频
                    </Button>
                </div>

                <Table
                    rowKey="fileName"
                    columns={columns}
                    dataSource={data || []}
                    loading={loading || downloadStatus.loading}
                    pagination={false}
                    scroll={{ y: "calc(100vh - 300px)" }}
                />
            </div>
        </Modal>
    );
}
